// @flow
import {
  pairsFromFilteredProducts,
  pairsForCatalogRetainingAtLeastOne,
} from '../modules/product';
import { makeScan, type Scan } from '../modules/scan';
import * as at from '../actions/action-types';
// types
import type { State as TopLevelState, Action } from '../store';
import type {
  FailToReceiveResourceAction,
  UpdateScanWithCodes,
  ResolveScan,
} from '../actions';

export type State = {
  scans: Scan[],
  isOpen: boolean,
};

export const scans = (state: TopLevelState): Scan[] =>
  state.barcodeScanning.scans;

export const isOpen = (state: TopLevelState): boolean =>
  state.barcodeScanning.isOpen;

export const initialState: State = {
  scans: [],
  isOpen: false,
};

const endpoints = ['PRODUCTS_FOR_SCAN', 'BARCODES_FOR_SCAN'];

const barcodeScanning = (
  state: State = initialState,
  action: Action,
): State => {
  // eslint-disable-next-line fp/no-let
  let scan;
  // eslint-disable-next-line fp/no-let
  let payload;

  switch (action.type) {
    case at.ADD_SCAN:
      return {
        ...state,
        scans: [...state.scans, { ...action.payload, isLoading: true }],
      };

    case at.RESOURCE_FAIL_TO_RECEIVE:
      if (
        action.payload.method !== 'GET' ||
        !endpoints.includes(action.payload.resourceName)
      )
        return state;

      // eslint-disable-next-line fp/no-mutation
      payload = ((action: any): FailToReceiveResourceAction).payload;
      // eslint-disable-next-line fp/no-mutation
      scan = (((payload.resourceId: any).scan
        ? (payload.resourceId: any).scan
        : (payload.resourceId: any)): Scan);

      return {
        ...state,
        scans: (state.scans.map((s) =>
          s.id === scan.id ? { ...s, error: true, isLoading: false } : s,
        ): Scan[]),
      };
    case at.UPDATE_SCAN_WITH_CODES:
      return (({
        payload: { scan: targetScan, codes },
      }: UpdateScanWithCodes) => {
        if (codes.length > 0) return state;
        return {
          ...state,
          scans: state.scans.map((foundScan: Scan): Scan =>
            foundScan.id === targetScan.id
              ? { ...foundScan, isLoading: false }
              : foundScan,
          ),
        };
      })(action);

    case at.RESOLVE_SCAN:
      return (({
        payload: { scan: targetScan, products, catalogKey },
      }: ResolveScan) => {
        if (products.length === 0) {
          return {
            ...state,
            scans: (state.scans.map((s) =>
              s.id === targetScan.id ? { ...s, isLoading: false } : s,
            ): Scan[]),
          };
        }

        return {
          ...state,
          scans: [
            ...state.scans.filter(({ id }) => id !== targetScan.id),
            ...pairsForCatalogRetainingAtLeastOne(
              catalogKey,
              pairsFromFilteredProducts(products),
            ).map((pair) => makeScan(targetScan.code, pair)),
          ],
        };
      })(action);
    case at.REMOVE_SCAN:
      return {
        ...state,
        scans: ((state.scans: Scan[]).filter(
          (p) => p !== action.payload,
        ): Scan[]),
      };

    case at.CLEAR_SCANS:
      return {
        ...state,
        scans: [],
      };

    case at.OPEN_BARCODE_SCANNING:
      return {
        ...state,
        variationPairs: [],
        isOpen: true,
      };

    case at.CLOSE_BARCODE_SCANNING:
    case at.ADD_SCANNED_PRODUCTS:
      return {
        ...state,
        variationPairs: [],
        scans: [],
        isOpen: false,
      };

    default:
      return state;
  }
};

export default barcodeScanning;
